import { useState, useEffect, useRef } from 'react';
import {
  Modal,
  ModalOverlay,
  ModalContent,
  ModalHeader,
  ModalFooter,
  ModalBody,
  ModalCloseButton,
  Button,
  FormControl,
  FormLabel,
  Input,
  Select,
  VStack,
  HStack,
  SimpleGrid,
  Checkbox,
  CheckboxGroup,
  Badge,
  NumberInput,
  NumberInputField,
  NumberInputStepper,
  NumberIncrementStepper,
  NumberDecrementStepper,
  useToast,
  Box,
  Text,
  Icon,
  Image,
  useColorModeValue,
  Tooltip,
  Collapse,
  useDisclosure,
  Divider,
} from '@chakra-ui/react';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faCloudUploadAlt, faInfoCircle, faChevronDown, faChevronUp, faPlus } from '@fortawesome/free-solid-svg-icons';
import { useCampaigns, useCampaignMutations } from '../../hooks/useCampaigns';
import CampaignModal from '../campaigns/CampaignModal';

// Helper component for labels with info tooltip
function LabelWithTooltip({ children, tooltip }) {
  return (
    <HStack spacing={1} display="inline-flex">
      <span>{children}</span>
      {tooltip && (
        <Tooltip label={tooltip} hasArrow placement="top" fontSize="xs">
          <span>
            <Icon as={FontAwesomeIcon} icon={faInfoCircle} boxSize={3} color="gray.400" cursor="help" />
          </span>
        </Tooltip>
      )}
    </HStack>
  );
}

const initialFormData = {
  name: '',
  campaignIds: [],
  type: 'image',
  width: 300,
  height: 250,
  destinationUrl: '',
  altText: '',
  weight: 1,
  htmlContent: '',
  textLinkText: '',
  textLinkStyle: 'default',
  // UTM Parameters (overrides campaign defaults)
  utmSource: '',
  utmMedium: '',
  utmCampaign: '',
  utmTerm: '',
  utmContent: '',
};

function AdModal({ isOpen, onClose, onSubmit, ad, isLoading }) {
  const [formData, setFormData] = useState(initialFormData);
  const [selectedFile, setSelectedFile] = useState(null);
  const [previewUrl, setPreviewUrl] = useState(null);
  const fileInputRef = useRef();
  const { data: campaigns, refetch: refetchCampaigns } = useCampaigns();
  const { createCampaign, loading: campaignLoading } = useCampaignMutations();
  const toast = useToast();
  const { isOpen: isUtmOpen, onToggle: onUtmToggle } = useDisclosure();
  const { isOpen: isCampaignModalOpen, onOpen: onCampaignModalOpen, onClose: onCampaignModalClose } = useDisclosure();
  const isEditing = !!ad;

  const handleCreateCampaign = async (data) => {
    try {
      const result = await createCampaign(data);
      toast({
        title: 'Campaign created',
        description: `${data.name} has been created successfully.`,
        status: 'success',
        duration: 3000,
      });
      await refetchCampaigns();
      // Auto-select the newly created campaign
      if (result?.data?.campaign_id) {
        setFormData(prev => ({
          ...prev,
          campaignIds: [...prev.campaignIds.filter(id => id !== result.data.campaign_id), result.data.campaign_id],
        }));
      }
      onCampaignModalClose();
    } catch (err) {
      toast({
        title: 'Error creating campaign',
        description: err.message,
        status: 'error',
        duration: 5000,
      });
    }
  };

  const dropzoneBg = useColorModeValue('gray.50', 'gray.700');
  const dropzoneBorder = useColorModeValue('gray.200', 'gray.600');

  useEffect(() => {
    if (ad) {
      setFormData({
        name: ad.ad_name || ad.name || '',
        campaignIds: ad.campaigns?.map(c => c.campaign_id) || (ad.campaign_id ? [ad.campaign_id] : []),
        type: ad.ad_type || ad.type || 'image',
        width: ad.width || 300,
        height: ad.height || 250,
        destinationUrl: ad.destination_url || ad.destinationUrl || '',
        altText: ad.alt_text || ad.altText || '',
        weight: ad.weight || 1,
        htmlContent: ad.html_content || '',
        textLinkText: ad.text_link_text || '',
        textLinkStyle: ad.text_link_style || 'default',
        // UTM Parameters
        utmSource: ad.utm_source || '',
        utmMedium: ad.utm_medium || '',
        utmCampaign: ad.utm_campaign || '',
        utmTerm: ad.utm_term || '',
        utmContent: ad.utm_content || '',
      });
      if (ad.file_path) {
        // Handle both absolute URLs and local file paths
        if (ad.file_path.startsWith('http://') || ad.file_path.startsWith('https://')) {
          setPreviewUrl(ad.file_path);
        } else {
          const serverUrl = window.location.origin.replace(':3058', ':3059');
          setPreviewUrl(`${serverUrl}/uploads/ads/${ad.file_path}`);
        }
      }
    } else {
      setFormData(initialFormData);
      setSelectedFile(null);
      setPreviewUrl(null);
    }
  }, [ad, isOpen]);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
  };

  const handleNumberChange = (name, value) => {
    setFormData(prev => ({ ...prev, [name]: parseInt(value) || 0 }));
  };

  const handleFileSelect = (e) => {
    const file = e.target.files?.[0];
    if (file) {
      const maxSize = formData.type === 'video' ? 50 * 1024 * 1024 : 5 * 1024 * 1024;
      if (file.size > maxSize) {
        toast({
          title: 'File too large',
          description: `Maximum file size is ${formData.type === 'video' ? '50MB' : '5MB'}`,
          status: 'error',
          duration: 3000,
        });
        return;
      }

      const imageTypes = ['image/jpeg', 'image/png', 'image/gif', 'image/webp'];
      const videoTypes = ['video/mp4', 'video/webm', 'video/ogg'];
      const allowedTypes = formData.type === 'video' ? videoTypes : imageTypes;

      if (!allowedTypes.includes(file.type)) {
        toast({
          title: 'Invalid file type',
          description: formData.type === 'video'
            ? 'Please upload an MP4, WebM, or OGG video'
            : 'Please upload a JPEG, PNG, GIF, or WebP image',
          status: 'error',
          duration: 3000,
        });
        return;
      }

      setSelectedFile(file);
      setPreviewUrl(URL.createObjectURL(file));

      // Try to get dimensions for images
      if (formData.type !== 'video') {
        const img = new window.Image();
        img.onload = () => {
          setFormData(prev => ({
            ...prev,
            width: img.width,
            height: img.height,
          }));
        };
        img.src = URL.createObjectURL(file);
      }
    }
  };

  const handleDrop = (e) => {
    e.preventDefault();
    const file = e.dataTransfer.files?.[0];
    if (file) {
      const fakeEvent = { target: { files: [file] } };
      handleFileSelect(fakeEvent);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!formData.name.trim()) {
      toast({
        title: 'Ad name is required',
        status: 'error',
        duration: 3000,
      });
      return;
    }

    if (!formData.campaignIds.length) {
      toast({
        title: 'Please select at least one campaign',
        status: 'error',
        duration: 3000,
      });
      return;
    }

    const needsFile = ['image', 'gif', 'video'].includes(formData.type);
    if (!isEditing && !selectedFile && needsFile) {
      toast({
        title: `Please upload ${formData.type === 'video' ? 'a video' : 'an image'}`,
        status: 'error',
        duration: 3000,
      });
      return;
    }

    if (formData.type === 'text' && !formData.textLinkText.trim()) {
      toast({
        title: 'Please enter link text',
        status: 'error',
        duration: 3000,
      });
      return;
    }

    const submitFormData = new FormData();
    submitFormData.append('name', formData.name);
    submitFormData.append('type', formData.type);
    submitFormData.append('width', formData.width);
    submitFormData.append('height', formData.height);
    submitFormData.append('destinationUrl', formData.destinationUrl);
    submitFormData.append('altText', formData.altText);
    submitFormData.append('weight', formData.weight);

    if (formData.type === 'html') {
      submitFormData.append('htmlContent', formData.htmlContent);
    }

    if (formData.type === 'text') {
      submitFormData.append('textLinkText', formData.textLinkText);
      submitFormData.append('textLinkStyle', formData.textLinkStyle);
    }

    if (selectedFile) {
      submitFormData.append('file', selectedFile);
    }

    // Campaign assignments (many-to-many)
    submitFormData.append('campaignIds', JSON.stringify(formData.campaignIds));

    // UTM Parameters (overrides)
    if (formData.utmSource) submitFormData.append('utmSource', formData.utmSource);
    if (formData.utmMedium) submitFormData.append('utmMedium', formData.utmMedium);
    if (formData.utmCampaign) submitFormData.append('utmCampaign', formData.utmCampaign);
    if (formData.utmTerm) submitFormData.append('utmTerm', formData.utmTerm);
    if (formData.utmContent) submitFormData.append('utmContent', formData.utmContent);

    // Use first campaignId for URL backward compat
    await onSubmit(formData.campaignIds[0], submitFormData, ad?.ad_id);
  };

  // Common ad sizes for quick selection
  const adSizes = [
    { label: 'Medium Rectangle', width: 300, height: 250 },
    { label: 'Leaderboard', width: 728, height: 90 },
    { label: 'Wide Skyscraper', width: 160, height: 600 },
    { label: 'Half Page', width: 300, height: 600 },
    { label: 'Large Rectangle', width: 336, height: 280 },
    { label: 'Mobile Banner', width: 320, height: 50 },
  ];

  const handleSizePreset = (width, height) => {
    setFormData(prev => ({ ...prev, width, height }));
  };

  const getFileAccept = () => {
    if (formData.type === 'video') return 'video/mp4,video/webm,video/ogg';
    return 'image/jpeg,image/png,image/gif,image/webp';
  };

  const getUploadText = () => {
    if (formData.type === 'video') return 'MP4, WebM, or OGG (max 50MB)';
    return 'PNG, JPG, GIF, or WebP (max 5MB)';
  };

  return (
    <Modal isOpen={isOpen} onClose={onClose} size="4xl">
      <ModalOverlay backdropFilter="blur(4px)" />
      <ModalContent borderRadius="2xl" mx={4}>
        <form onSubmit={handleSubmit}>
          <ModalHeader>{isEditing ? 'Edit Ad' : 'Upload New Ad'}</ModalHeader>
          <ModalCloseButton />
          <ModalBody>
            <SimpleGrid columns={{ base: 1, md: 2 }} spacing={6}>
              {/* Left Column */}
              <VStack spacing={4} align="stretch">
                <FormControl isRequired>
                  <FormLabel>
                    <LabelWithTooltip tooltip="A unique name to identify this ad creative">
                      Ad Name
                    </LabelWithTooltip>
                  </FormLabel>
                  <Input
                    name="name"
                    value={formData.name}
                    onChange={handleChange}
                    placeholder="Enter ad name"
                    borderRadius="xl"
                  />
                </FormControl>

                <FormControl>
                  <FormLabel>
                    <HStack spacing={2}>
                      <LabelWithTooltip tooltip="Assign this ad to one or more campaigns">
                        Campaigns
                      </LabelWithTooltip>
                      {formData.campaignIds.length > 0 && (
                        <Badge colorScheme="brand" borderRadius="full" fontSize="xs">
                          {formData.campaignIds.length} selected
                        </Badge>
                      )}
                    </HStack>
                  </FormLabel>
                  <Box
                    border="1px solid"
                    borderColor={dropzoneBorder}
                    borderRadius="xl"
                    maxH="160px"
                    overflowY="auto"
                    p={3}
                  >
                    <CheckboxGroup
                      value={formData.campaignIds.map(String)}
                      onChange={(vals) => setFormData(prev => ({ ...prev, campaignIds: vals.map(Number) }))}
                    >
                      <VStack align="start" spacing={2}>
                        {campaigns?.map(c => (
                          <Checkbox
                            key={c.campaign_id}
                            value={String(c.campaign_id)}
                            size="sm"
                            colorScheme="brand"
                          >
                            <HStack spacing={2}>
                              <Text fontSize="sm">{c.campaign_name}</Text>
                              {c.advertiser_name && (
                                <Badge fontSize="xs" colorScheme="gray" borderRadius="full">
                                  {c.advertiser_name}
                                </Badge>
                              )}
                            </HStack>
                          </Checkbox>
                        ))}
                        {(!campaigns || campaigns.length === 0) && (
                          <Text fontSize="sm" color="gray.500">No campaigns available</Text>
                        )}
                      </VStack>
                    </CheckboxGroup>
                  </Box>
                  <HStack mt={2}>
                    <Tooltip label="Create new campaign" hasArrow>
                      <Button
                        size="xs"
                        leftIcon={<FontAwesomeIcon icon={faPlus} />}
                        colorScheme="brand"
                        variant="outline"
                        borderRadius="full"
                        onClick={onCampaignModalOpen}
                      >
                        New Campaign
                      </Button>
                    </Tooltip>
                  </HStack>
                </FormControl>

                <FormControl>
                  <FormLabel>
                    <LabelWithTooltip tooltip="Format of the ad creative. Choose based on your media assets.">
                      Ad Type
                    </LabelWithTooltip>
                  </FormLabel>
                  <Select
                    name="type"
                    value={formData.type}
                    onChange={handleChange}
                    borderRadius="xl"
                  >
                    <option value="image">Image (PNG, JPG, WebP)</option>
                    <option value="gif">Animated GIF</option>
                    <option value="video">Video (MP4, WebM)</option>
                    <option value="html">HTML5 / Rich Media</option>
                    <option value="text">Text Link</option>
                  </Select>
                </FormControl>

                {/* File Upload for image/gif/video */}
                {['image', 'gif', 'video'].includes(formData.type) && (
                  <FormControl>
                    <FormLabel>{formData.type === 'video' ? 'Video File' : 'Ad Image'}</FormLabel>
                    <Box
                      p={4}
                      bg={dropzoneBg}
                      border="2px dashed"
                      borderColor={dropzoneBorder}
                      borderRadius="xl"
                      textAlign="center"
                      cursor="pointer"
                      onClick={() => fileInputRef.current?.click()}
                      onDrop={handleDrop}
                      onDragOver={(e) => e.preventDefault()}
                      _hover={{ borderColor: 'brand.500' }}
                      transition="all 0.2s"
                    >
                      {previewUrl ? (
                        <VStack spacing={2}>
                          {formData.type === 'video' ? (
                            <video src={previewUrl} style={{ maxHeight: '120px', borderRadius: '8px' }} />
                          ) : (
                            <Image src={previewUrl} alt="Ad preview" maxH="120px" objectFit="contain" borderRadius="lg" />
                          )}
                          <Text fontSize="sm" color="gray.500">Click or drag to replace</Text>
                        </VStack>
                      ) : (
                        <VStack spacing={2}>
                          <Icon boxSize={8} color="gray.400">
                            <FontAwesomeIcon icon={faCloudUploadAlt} />
                          </Icon>
                          <Text fontWeight="500" fontSize="sm">Click to upload or drag and drop</Text>
                          <Text fontSize="xs" color="gray.500">{getUploadText()}</Text>
                        </VStack>
                      )}
                      <Input
                        ref={fileInputRef}
                        type="file"
                        accept={getFileAccept()}
                        onChange={handleFileSelect}
                        display="none"
                      />
                    </Box>
                  </FormControl>
                )}

                {/* HTML Content */}
                {formData.type === 'html' && (
                  <FormControl>
                    <FormLabel>HTML Content</FormLabel>
                    <Input
                      as="textarea"
                      name="htmlContent"
                      value={formData.htmlContent}
                      onChange={handleChange}
                      placeholder="<div>Your HTML5 ad code</div>"
                      borderRadius="xl"
                      rows={5}
                      fontFamily="mono"
                      fontSize="sm"
                    />
                  </FormControl>
                )}

                {/* Text Link Fields */}
                {formData.type === 'text' && (
                  <>
                    <FormControl isRequired>
                      <FormLabel>Link Text</FormLabel>
                      <Input
                        name="textLinkText"
                        value={formData.textLinkText}
                        onChange={handleChange}
                        placeholder="Click here for amazing deals!"
                        borderRadius="xl"
                      />
                    </FormControl>
                    <FormControl>
                      <FormLabel>Link Style</FormLabel>
                      <Select
                        name="textLinkStyle"
                        value={formData.textLinkStyle}
                        onChange={handleChange}
                        borderRadius="xl"
                      >
                        <option value="default">Default</option>
                        <option value="bold">Bold</option>
                        <option value="button">Button Style</option>
                      </Select>
                    </FormControl>
                  </>
                )}
              </VStack>

              {/* Right Column */}
              <VStack spacing={4} align="stretch">
                {formData.type !== 'text' && (
                  <FormControl>
                    <FormLabel>
                      <LabelWithTooltip tooltip="Standard IAB ad sizes. Match this to your zone sizes for best results.">
                        Ad Size (IAB Presets)
                      </LabelWithTooltip>
                    </FormLabel>
                    <HStack spacing={2} mb={2} flexWrap="wrap">
                      {adSizes.map(size => (
                        <Button
                          key={size.label}
                          size="xs"
                          variant={formData.width === size.width && formData.height === size.height ? 'solid' : 'outline'}
                          colorScheme="brand"
                          onClick={() => handleSizePreset(size.width, size.height)}
                          borderRadius="full"
                          mb={1}
                        >
                          {size.label}
                        </Button>
                      ))}
                    </HStack>
                    <SimpleGrid columns={2} spacing={4}>
                      <NumberInput
                        value={formData.width}
                        onChange={(val) => handleNumberChange('width', val)}
                        min={1}
                      >
                        <NumberInputField borderRadius="xl" placeholder="Width" />
                        <NumberInputStepper>
                          <NumberIncrementStepper />
                          <NumberDecrementStepper />
                        </NumberInputStepper>
                      </NumberInput>
                      <NumberInput
                        value={formData.height}
                        onChange={(val) => handleNumberChange('height', val)}
                        min={1}
                      >
                        <NumberInputField borderRadius="xl" placeholder="Height" />
                        <NumberInputStepper>
                          <NumberIncrementStepper />
                          <NumberDecrementStepper />
                        </NumberInputStepper>
                      </NumberInput>
                    </SimpleGrid>
                  </FormControl>
                )}

                <FormControl isRequired>
                  <FormLabel>
                    <LabelWithTooltip tooltip="Where users go when they click the ad. Include UTM parameters for tracking.">
                      Destination URL
                    </LabelWithTooltip>
                  </FormLabel>
                  <Input
                    name="destinationUrl"
                    value={formData.destinationUrl}
                    onChange={handleChange}
                    placeholder="https://example.com/landing?utm_source=..."
                    borderRadius="xl"
                  />
                </FormControl>

                <SimpleGrid columns={2} spacing={4}>
                  <FormControl>
                    <FormLabel>
                      <LabelWithTooltip tooltip="Accessibility text shown if image fails to load. Good for SEO.">
                        Alt Text
                      </LabelWithTooltip>
                    </FormLabel>
                    <Input
                      name="altText"
                      value={formData.altText}
                      onChange={handleChange}
                      placeholder="Ad description"
                      borderRadius="xl"
                    />
                  </FormControl>
                  <FormControl>
                    <FormLabel>
                      <LabelWithTooltip tooltip="Relative weight for rotation. Higher weight = shown more often vs other ads.">
                        Weight
                      </LabelWithTooltip>
                    </FormLabel>
                    <NumberInput
                      value={formData.weight}
                      onChange={(val) => handleNumberChange('weight', val)}
                      min={1}
                      max={100}
                    >
                      <NumberInputField borderRadius="xl" />
                      <NumberInputStepper>
                        <NumberIncrementStepper />
                        <NumberDecrementStepper />
                      </NumberInputStepper>
                    </NumberInput>
                  </FormControl>
                </SimpleGrid>
              </VStack>
            </SimpleGrid>

            {/* UTM Parameters Section */}
            <Box mt={6}>
              <Divider mb={4} />
              <Button
                variant="ghost"
                onClick={onUtmToggle}
                width="100%"
                justifyContent="space-between"
                rightIcon={<FontAwesomeIcon icon={isUtmOpen ? faChevronUp : faChevronDown} />}
                mb={2}
              >
                <HStack>
                  <Text fontWeight="500">UTM Overrides</Text>
                  <Text fontSize="sm" color="gray.500">(Optional)</Text>
                </HStack>
              </Button>
              <Collapse in={isUtmOpen}>
                <Box p={4} bg="gray.50" borderRadius="xl" _dark={{ bg: 'gray.700' }}>
                  <Text fontSize="sm" color="gray.500" mb={4}>
                    Override campaign UTM defaults for this specific ad. Leave blank to use campaign defaults.
                  </Text>
                  <SimpleGrid columns={{ base: 1, md: 3 }} spacing={4}>
                    <FormControl>
                      <FormLabel fontSize="sm">
                        <LabelWithTooltip tooltip="Identifies which site sent the traffic">
                          utm_source
                        </LabelWithTooltip>
                      </FormLabel>
                      <Input
                        name="utmSource"
                        value={formData.utmSource}
                        onChange={handleChange}
                        placeholder="e.g., statfalcon"
                        size="sm"
                        borderRadius="lg"
                      />
                    </FormControl>
                    <FormControl>
                      <FormLabel fontSize="sm">
                        <LabelWithTooltip tooltip="Identifies what type of link was used">
                          utm_medium
                        </LabelWithTooltip>
                      </FormLabel>
                      <Input
                        name="utmMedium"
                        value={formData.utmMedium}
                        onChange={handleChange}
                        placeholder="e.g., banner"
                        size="sm"
                        borderRadius="lg"
                      />
                    </FormControl>
                    <FormControl>
                      <FormLabel fontSize="sm">
                        <LabelWithTooltip tooltip="Identifies a specific campaign or promotion">
                          utm_campaign
                        </LabelWithTooltip>
                      </FormLabel>
                      <Input
                        name="utmCampaign"
                        value={formData.utmCampaign}
                        onChange={handleChange}
                        placeholder="e.g., summer_2024"
                        size="sm"
                        borderRadius="lg"
                      />
                    </FormControl>
                    <FormControl>
                      <FormLabel fontSize="sm">
                        <LabelWithTooltip tooltip="Identifies paid search keywords">
                          utm_term
                        </LabelWithTooltip>
                      </FormLabel>
                      <Input
                        name="utmTerm"
                        value={formData.utmTerm}
                        onChange={handleChange}
                        placeholder="e.g., running+shoes"
                        size="sm"
                        borderRadius="lg"
                      />
                    </FormControl>
                    <FormControl gridColumn={{ md: 'span 2' }}>
                      <FormLabel fontSize="sm">
                        <LabelWithTooltip tooltip="Differentiates similar content (e.g., banner_v1, header_cta)">
                          utm_content
                        </LabelWithTooltip>
                      </FormLabel>
                      <Input
                        name="utmContent"
                        value={formData.utmContent}
                        onChange={handleChange}
                        placeholder="e.g., hero_banner"
                        size="sm"
                        borderRadius="lg"
                      />
                    </FormControl>
                  </SimpleGrid>
                </Box>
              </Collapse>
            </Box>
          </ModalBody>
          <ModalFooter gap={3}>
            <Button variant="ghost" onClick={onClose} borderRadius="xl">
              Cancel
            </Button>
            <Button
              type="submit"
              colorScheme="brand"
              isLoading={isLoading}
              borderRadius="xl"
            >
              {isEditing ? 'Save Changes' : 'Create Ad'}
            </Button>
          </ModalFooter>
        </form>
      </ModalContent>

      {/* Nested Campaign Modal */}
      <CampaignModal
        isOpen={isCampaignModalOpen}
        onClose={onCampaignModalClose}
        onSubmit={handleCreateCampaign}
        isLoading={campaignLoading}
      />
    </Modal>
  );
}

export default AdModal;
